import {mat4, vec3, vec4} from 'gl-matrix';
const Stats = require('stats-js');
import * as DAT from 'dat.gui';
import Icosphere from './geometry/Icosphere';
import Square from './geometry/Square';
import Cube from './geometry/Cube';
import OpenGLRenderer from './rendering/gl/OpenGLRenderer';
import Camera from './Camera';
import {setGL} from './globals';
import ShaderProgram, {Shader} from './rendering/gl/ShaderProgram';

// Define an object with application parameters and button callbacks
// This will be referred to by dat.GUI's functions that add GUI elements.
const controls = {
  tesselations: 5,
  'Load Scene': loadScene, // A function pointer, essentially
  colorpicker:  [0, 210, 255],
  cubeSubdivisions: 16, 
  waveFrequency: 2.0,
  warpAmount: 0.3,
  dispAmount: 0.15,
};

let icosphere: Icosphere;
let square: Square;
let cube: Cube;
let prevTesselations: number = 5;
let prevCubeSubdivisions: number = 16;

function loadScene() {
  icosphere = new Icosphere(vec3.fromValues(0, 0, 0), 1, controls.tesselations);
  icosphere.create();
  square = new Square(vec3.fromValues(0, 0, 0));
  square.create();
  cube = new Cube(vec3.fromValues(0, 0, 0), controls.cubeSubdivisions);
  cube.create();
}

function main() {
  // Initial display for framerate
  const stats = Stats();
  stats.setMode(0);
  stats.domElement.style.position = 'absolute';
  stats.domElement.style.left = '0px';
  stats.domElement.style.top = '0px';
  document.body.appendChild(stats.domElement);

  // Add controls to the gui
  const gui = new DAT.GUI();
  gui.domElement.style.transform = 'scale(1.7)';
  gui.domElement.style.transformOrigin = 'top right';
  gui.add(controls, 'tesselations', 0, 8).step(1);
  gui.add(controls, 'Load Scene');
  gui.addColor(controls, 'colorpicker');
  gui.add(controls, 'cubeSubdivisions', 1, 64).step(1);
  gui.add(controls, 'waveFrequency', 0.5, 20);
  gui.add(controls, 'warpAmount', 0, 1);
  gui.add(controls, 'dispAmount', 0, 0.5);

  // get canvas and webgl context
  const canvas = <HTMLCanvasElement> document.getElementById('canvas');
  const gl = <WebGL2RenderingContext> canvas.getContext('webgl2');
  if (!gl) {
    alert('WebGL 2 not supported!');
  }
  // `setGL` is a function imported above which sets the value of `gl` in the `globals.ts` module.
  // Later, we can import `gl` from `globals.ts` to access it
  setGL(gl);

  // Initial call to load scene
  loadScene();

  const camera = new Camera(vec3.fromValues(0, 0, 5), vec3.fromValues(0, 0, 0));

  const renderer = new OpenGLRenderer(canvas);
  renderer.setClearColor(0.2, 0.2, 0.2, 1);
  gl.enable(gl.DEPTH_TEST);

  const lambert = new ShaderProgram([
    new Shader(gl.VERTEX_SHADER, require('./shaders/lambert-vert.glsl')),
    new Shader(gl.FRAGMENT_SHADER, require('./shaders/lambert-frag.glsl')),
  ]);

  // Model matrix, rebuilt every frame so the cube tumbles slowly about two
  // axes.
  const model = mat4.create();

  // This function will be called every frame
  function tick(timeMs: number) {
    const time = timeMs / 1000.0;
    mat4.identity(model);
    mat4.rotateY(model, model, time * 0.15);
    mat4.rotateX(model, model, time * 0.1);
    lambert.setTime(time);
    lambert.setWaveFreq(controls.waveFrequency);
    lambert.setWarpAmount(controls.warpAmount);
    lambert.setDispAmount(controls.dispAmount);
    camera.update();
    stats.begin();
    gl.viewport(0, 0, window.innerWidth, window.innerHeight);
    renderer.clear();
    if(controls.tesselations != prevTesselations)
    {
      prevTesselations = controls.tesselations;
      icosphere = new Icosphere(vec3.fromValues(0, 0, 0), 1, prevTesselations);
      icosphere.create();
    }
    if(controls.cubeSubdivisions != prevCubeSubdivisions)
    {
      prevCubeSubdivisions = controls.cubeSubdivisions;
      cube = new Cube(vec3.fromValues(0, 0, 0), prevCubeSubdivisions);
      cube.create();
    }
    
    const color = vec4.fromValues(
      controls.colorpicker[0] / 255.0,
      controls.colorpicker[1] / 255.0,
      controls.colorpicker[2] / 255.0,
      1.0,
    );
    renderer.render(camera, lambert, model, color, [
      // icosphere,
      // square,
      cube,
    ]);
    stats.end();

    // Tell the browser to call `tick` again whenever it renders a new frame
    requestAnimationFrame(tick);
  }

  window.addEventListener('resize', function() {
    renderer.setSize(window.innerWidth, window.innerHeight);
    camera.setAspectRatio(window.innerWidth / window.innerHeight);
    camera.updateProjectionMatrix();
  }, false);

  renderer.setSize(window.innerWidth, window.innerHeight);
  camera.setAspectRatio(window.innerWidth / window.innerHeight);
  camera.updateProjectionMatrix();

  // Start the render loop
  requestAnimationFrame(tick);
}

main();
